import Vue from 'vue';
import Vuex, { Module } from 'vuex';
import client from '../../endpoints';
import {
  AccountOpeningMetadataResponse,
  AccountOpeningResponse,
  AddAcknowledgementsRequest,
  AddFeesRequest,
  AddInvestmentProfileRequest,
  AddPersonalDetailsRequest,
  SaveTentativeAccountRequest,
  SourceOfFunds,
  StartAccountOpeningRequest,
} from '../../endpoints/types';
import {
  countries,
  currencies,
  levelsOfEducation,
  maritalStatuses,
  previousExperienceAverageVolumes,
  previousExperienceExperiences,
  previousExperienceTransactions,
  staticToOptions,
} from './static';
import {
  findInstantAndClassic,
  findMetadataOption,
  findReasonOption,
  getAccountType,
  getFeeSchedule,
  getStageAccountType,
} from './support';
import {
  AccountHolderDetails,
  AddressDetails,
  DisbursementAccountDetails,
  OpenAccountError,
  OpenAccountInvestmentServiceOption,
  OpenAccountMetadata,
  OpenAccountMetadataOption,
  OpenAccountState,
  Appropriateness,
  CountryCodes,
} from './types';



import { handleError } from '@/utils/helperFunctions';
import countryCodes, { countryCodesList, lookupCountryCode } from '@/utils/countryCodes';
import { Interval } from '@/store/openAccount/types';
import store from '..';
import { OpenAccountType } from '@/utils/accountTypes';
import jwtDecode from 'jwt-decode';
import {
  isAmaClassicFlexOpenAccountContext,
  platformProcessingFeesAreEqual,
} from '@/utils/amaClassicFlexOpenAccount';

const frequency: Interval[] = [
  { value: 1, text: 'Monthly' },
  { value: 3, text: 'Quarterly' },
  { value: 6, text: 'Half Yearly' },
  { value: 12, text: 'Yearly' },
];

const feeOptions: OpenAccountMetadataOption[] = [
  { value: 3, text: 'ClassicFlex', metadata: {} },
  { value: 7, text: 'AMA ClassicFlex', metadata: {} },
];

Vue.use(Vuex);

const log = (...data: readonly any[]) =>
  process.env.NODE_ENV !== 'production' && console.warn(...data);
const err = (...data: readonly any[]) =>
  process.env.NODE_ENV !== 'production' && console.error(...data);

const defaultState = (): OpenAccountState => {
  return {
    // Globally capture errors so we can display them consistently.
    error: null,
    isGeneratingPDF: false,
    isSignReview: false,
    isManualReview: false,
    existingForms: [],
    // Metadata loaded to populate dropdowns and other selectable objects.
    metadata: {
      accountTypes: [],
      trustees: [],
      dfmProviders: [],
      instantOrTermAccessAccounts: [
        { value: 1, text: 'Instant Access', metadata: {} },
        { value: 2, text: 'Term Account', metadata: {} },
      ],
      classicOrFlexibleFeeScheduleOptions: feeOptions,
      titles: [
        { value: 1, text: 'Mr', metadata: {} },
        { value: 2, text: 'Miss', metadata: {} },
        { value: 3, text: 'Mrs', metadata: {} },
        { value: 4, text: 'Dr', metadata: {} },
      ],
      countries: staticToOptions(countryCodesList.map((f: any) => f.name)),
      nationalities: staticToOptions(countries),
      maritalStatuses: staticToOptions(maritalStatuses),
      levelsOfEducation: staticToOptions(levelsOfEducation),
      currencies: staticToOptions(currencies),
      incomeSources: [],
      investmentProfiles: [],
      previousExperiences: [],
      riskProfile: [],
      investmentServices: [],
      defaultFeesAndCharges: {
        platformProcessingFee: 0,
        annualAccountFeeAdministration: 0,
        annualAccountFeeRenumeration: 0,
        annualServiceFee: 0,
        initialFee: 0,
        etfEntryFee: 0,
        equitiesEntryFee: 0,
        minimumCashBalance: 0,
        customFeeStructure: false,
        IsTermAccount: false,
        TA_PPEDeductionFee: 0,
        TA_PPEDeductionFeeYears: 10,
        TA_ProductFeeEstablishPeriod: 10,
        TA_ProductFeeQuarterServiceFee: 0,
        TA_ProductFeeQuarterEncashPerc: 0,
        TA_ProductFeeInitialFee: 0,
        TA_BusinessConditionCMA: 0,
        TA_BusinessConditionEarlyEncashYear: 0,
        TA_BusinessConditionInitialPremium: 0,
        TA_BusinessConditionInitialPremiumQuarter: 0,
        TA_BusinessConditionInitialPremiumQuarterYear: 0,
        TA_ProductFeeQuarterAdminCharge: 0,
        TA_FlexibleAnnualServiceFeeRate: 0,
      },
      discretionaryAssetManagers: [],
      previousExperienceExperiences: staticToOptions(
        previousExperienceExperiences,
      ),
      previousExperienceTransactions: staticToOptions(
        previousExperienceTransactions,
      ),
      previousExperienceAverageVolumes: staticToOptions(
        previousExperienceAverageVolumes,
      ),
    },
    navigationActive: false,
    // Form stages.
    stages: [
      {
        title: 'Account Type Selection',
        steps: [{ title: 'Account type' }, { title: 'Confirm details' }],
        valid: true,
      },
      {
        title: 'Personal Details',
        steps: [
          { title: 'First account holder details' },

          { title: 'Correspondence information' },
          { title: 'Disbursement account details' },
        ],
        valid: false,
      },
      {
        title: 'Investment Profile',
        steps: [{ title: 'Risk profile' }, { title: 'Appropriateness test' }],
        valid: false,
      },
      { title: 'Fees and Charges', steps: [], valid: false },
      {
        title: 'Acknowledgements',
        steps: [
          { title: 'Discretionary management services' },
          { title: 'Business conditions' },
          { title: 'Appropriateness test' },
        ],
        valid: false,
      },
      { title: 'Sign', steps: [], valid: false },
      { title: 'Finish', steps: [], valid: false },
    ],
    // Current position within the form expressed as a tuple [stage, step].
    currentPosition: [0, 0],
    lastStage: 0,
    // Response from starting the account opening flow.
    accountOpeningResponse: null,
    // Tentative accounts saved to the server and assigned an ID.
    accountTypes: [
      {
        accountType: null,
        trustee: null,
        dfmProvider: null,
        instantAccessOrTermAccount: null,
        classicOrFlexibleFeeSchedule: null,
        IsTermAccount: false,
      },
    ],
    // Personal details.
    personalDetails: {
      primaryAccountHolder: {
        title: null,
        firstName: '',
        lastName: '',
        address: {
          address: '',
          suburb: '',
          postcode: '',
          country: null,
          number: '',
        },
        hasRecentMove: false,
        previousAddress: null,
        emailAddress: '',
        secondaryEmailAddress: '',
        primaryCountryCode: {
          value: '+1',
          text: '+1',
          name: 'Malaysia',
        },
        primaryPhoneNumber: '+1-',
        secondaryCountryCode: {
          name: 'Canada',
          value: '+1',
          text: '+1',
        },
        secondaryPhoneNumber: '+1-',
        fax: '',
        dobDate: '',
        dobMonth: '',
        dobYear: '',
        placeOfBirth: '',
        countryOfBirthId: null,
        nationalityId: null,
        passportNumber: null,
        maritalStatusId: null,
        levelOfEducationId: null,
        countryOfFiscalResidence: {
          country: null,
          tin: '',
          reasonForNoTin: null,
        },
        leiRequired: false,
        lei: '',
        politicallyExposedPersons: false,
        IsUSCitizen: false,
        ReportingTermsAgreed: false,
        CorrespondenceTermsAgreed: false,
        OccupationId: null,
        Occupation: '',
        EmploymentStatusId: null,
        EmployerName: '',
        EmployerNumber: '',
        DurationOfEmployment: null,
        SectorOfEmployment: null,
        RetiredRemarks: null,
        agreeToBusinessConditionTerms: false,
        VATNumber: '',
      },
      secondaryAccountHolder: null,
      usePrimaryMailingAddress: true,
      useSecondaryMailingAddress: false,
      alternativeMailingAddress: {
        address: '',
        suburb: '',
        postcode: '',
        country: null,
        number: '',
      },
      // agreeToTerms: false,
      tentativeDisbursementAccounts: [
        {
          currencyId: null,
          name: '',
          iban: '',
          bankSortCode: '',
          bankName: '',
          country: null,
          DirectDebitDetails: {
            active: false,
            amount: 0,
            currency: null,
            date: null,
            frequency: null,
          },
          RegularPaymentsDetails: {
            active: false,
            amount: 0,
            currency: null,
            date: null,
            frequency: null,
          },
        },
      ],
      disbursementAccounts: [],
      DirectDebitDetails: {
        active: false,
        amount: 0,
        currency: null,
        date: null,
        frequency: null,
      },
      RegularPaymentsDetails: {
        active: false,
        amount: 0,
        currency: null,
        date: null,
        frequency: null,
      },
    },

    investmentProfile: {
      AccountOpeningId: null,
      CurrencyId: null,
      DisposableAssetsRangeId: null,
      RiskProfileId: null,
      AppropriatenessExemption: false,
      NotInterested: false,
      NotSufficient: false,
      Sufficient: false,
      incomeSources: [],
      Appropriatenesses: [],
      SourceOfFunds: [],
      agreeToAppropriatenessTestTerms: false,
      NaturalPersonTerms: false,

      investmentCurrency: null,
      investmentAmount: null,
      PlannedInvestmentFrequency: null,
      PlannedInvestmentFrequencyOther: null,
      investmentProfile: null,
      seekExemption: false,
      previousExperience: [],
      pastInvestmentServices: [],
    },
    feesAndCharges: {
      customFeeStructure: false,
      platformProcessingFee: 0,
      annualAccountFeeAdministration: 0,
      annualAccountFeeRenumeration: 0,
      annualServiceFee: 0,
      initialFee: 0,
      etfEntryFee: 0,
      equitiesEntryFee: 0,
      structuredNotesEntryFee: 0,
      minimumCashBalance: 0,
      IsTermAccount: false,
      TA_PPEDeductionFee: 0,
      TA_PPEDeductionFeeYears: 10,
      TA_ProductFeeEstablishPeriod: 10,
      TA_ProductFeeQuarterServiceFee: 0,
      TA_ProductFeeQuarterEncashPerc: 0,
      TA_ProductFeeInitialFee: 0,
      TA_BusinessConditionCMA: 0,
      TA_BusinessConditionEarlyEncashYear: 0,
      TA_BusinessConditionInitialPremium: 0,
      TA_BusinessConditionInitialPremiumQuarter: 0,
      TA_BusinessConditionInitialPremiumQuarterYear: 0,
      TA_FlexibleAnnualServiceFeeRate: 0,
      TA_ProductFeeQuarterAdminCharge: 0,
      //
    },
    acknowledgements: {
      discretionaryAssetManager: null,
      agreeToDiscretionaryManagementTerms: false,
      AppointDAM: false,
    },
    signingPDFs: [],
    generatedAccountOpeningFiles: [],
    isManual: false,
    uploadedDocuments: null,
    sourceOfFundsModal: false,
    state: undefined,
    TaxCertForm: false,
    SOFForm: false,
    accountProvisioning: {
      selectedBostonId: null,
      isAdvisorListOpen: false,
    },
    kyc: {
      isModalOpen: false,
      isAcknowledged: false,
    },
    fieldErrors: {}
  };
};

const module: Module<OpenAccountState, any> = {
  state: defaultState(),
  mutations: {
    setStagesForAccount(state) {
      if (state.accountTypes[0].accountType?.value) {
        const stages = getStageAccountType(
          getAccountType(state.accountTypes[0].accountType?.value),
        );
        state.stages = stages;
      }
    },
    setAccountOpeningError(
      state,
      payload: OpenAccountError | string[] | null,
    ): void {
      state.error = payload;
    },

    setCurrentPosition(state, payload: [number, number]): void {
      log(`Updating position to stage ${payload[0]} and step ${payload[1]}.`);
      if (state.stages[payload[0] - 1]) {
        if (!state.stages[payload[0] - 1].valid) {
          return;
        }

        if (state.currentPosition[0] !== payload[0]) {
          // Go back to the top of the page during stage transitions.
          window.scrollTo(0, 0);
        }

        state.currentPosition = payload;
      }
    },

    validateStages(state, payload: { lastStage: number }) {
      if (payload.lastStage === 1) {
        state.stages[1].valid = true;
      }
      for (let i = 0; i < payload.lastStage; i++) {
        state.stages[i].valid = true;
      }
    },

    forceCurrentPosition(state, payload: [number, number]): void {
      log(`Forcing position to stage ${payload[0]} and step ${payload[1]}.`);
      if (!state.stages[payload[0] - 1].valid) {
        log('You have not completed the prev stages');
        return;
      }

      if (state.currentPosition[0] !== payload[0]) {
        // Go back to the top of the page during stage transitions.
        window.scrollTo(0, 0);
      }

      setTimeout(() => {
        // Move to the target step.
        const stepHeader: Element | null = document.querySelector(
          `section[data-stage="${payload[0]}"][data-step="${payload[1]}"]`,
        );

        if (stepHeader) {
          stepHeader.scrollIntoView();
        }
      }, 50);

      state.currentPosition = payload;
    },

    setAccountOpeningMetadata(
      state,
      payload: Partial<OpenAccountMetadata>,
    ): void {
      state.metadata = { ...state.metadata, ...payload };
    },

    setExistingForms(state, payload: any[]): void {
      state.existingForms = payload;
    },
    setSigneeDetails(state, payload) {
      state.signingPDFs = payload;
    },

    setAccountOpeningResponse(state, response: AccountOpeningResponse): void {
      try {
        state.accountOpeningResponse = response;
        // Reconcile account types.
        if (
          response.Data.AccountTypes &&
          response.Data.AccountTypes.length > 0
        ) {
          state.accountTypes = response.Data.AccountTypes.map((entry) => {
            const accountType = state.metadata.accountTypes.find(
              (item: { value: number }) => item.value === entry.AccountTypeId,
            );
            const dfmProvider = state.metadata.dfmProviders.find(
              (item: { value: number }) => item.value === entry.DFMProviderId,
            );
            const trustee = state.metadata.trustees.find(
              (item: { value: number | null }) =>
                item.value === entry.QSTProviderId,
            );
            const instantAccessOrTermAccount = state.metadata.instantOrTermAccessAccounts.find(
              (item: { value: number }) => {
                return (
                  item.value ===
                  findInstantAndClassic(entry.FeeSchedulesId).instantOrTerm
                );
              },
            );
            const classicOrFlexibleFeeSchedule = feeOptions.find(
              (item: { value: number }) => {
                return (
                  item.value ===
                  findInstantAndClassic(entry.FeeSchedulesId).classicOrFlexible
                );
              },
            );

            return {
              id: entry.ID,
              accountType: accountType ? accountType : null,
              trustee: trustee ? trustee : null,
              dfmProvider: dfmProvider ? dfmProvider : null,
              instantAccessOrTermAccount: instantAccessOrTermAccount
                ? instantAccessOrTermAccount
                : null,
              classicOrFlexibleFeeSchedule: classicOrFlexibleFeeSchedule
                ? classicOrFlexibleFeeSchedule
                : null,
            };
          }) as any;
        }

        if (state.accountTypes.length === 0) {
          state.accountTypes = [
            {
              accountType: null,
              trustee: null,
              dfmProvider: null,
              instantAccessOrTermAccount: null,
              classicOrFlexibleFeeSchedule: null,
              IsTermAccount: false,
            },
          ];
        }

        const primaryAccountHolders = response.Data.PersonalDetails.filter(
          (entry) => entry.IsPrimary,
        );
        const secondaryAccountHolders = response.Data.PersonalDetails.filter(
          (entry) => !entry.IsPrimary,
        );

        const primaryAccountHolder =
          primaryAccountHolders.length > 0
            ? primaryAccountHolders[primaryAccountHolders.length - 1]
            : null;
        const secondaryAccountHolder =
          secondaryAccountHolders.length > 0
            ? secondaryAccountHolders[secondaryAccountHolders.length - 1]
            : null;


        const alternativeMailingAddress: any =
          response.Data.Addresses.length > 0
          ? response.Data.Addresses.filter((item) => item.AddressType === 'Correspondence')[0]
          : null;

        const accountHolderFromResponse = (
          ah: AccountOpeningResponse['Data']['PersonalDetails'][0],
        ): AccountHolderDetails => {
          const dob = new Date(ah.BirthDate);

          const addressType: string = ah.IsPrimary ? OpenAccountType.PRIMARY : OpenAccountType.SECONDARY;
          const primaryAddress: any =
            response.Data.Addresses.length > 0
            ?  response.Data.Addresses.filter((item) => item.AddressType === addressType)[0]
            : null;
          const formerAddress =
            response.Data.Addresses.length > 1
              ? response.Data.Addresses[1]
              : null;

          const fiscalResidence =
            response.Data.FiscalResidences[ah.IsPrimary ? 0 : 1];

          return {
            id: ah.ID,
            title: findMetadataOption(ah.TitleId, state.metadata.titles),
            firstName: ah.FirstName,
            lastName: ah.LastName,
            address: {
              address: primaryAddress ? primaryAddress.Street : '',
              country: primaryAddress
                ? findMetadataOption(
                    primaryAddress.CountryId,
                    state.metadata.countries,
                  )
                : null,
              postcode: primaryAddress ? primaryAddress.PostCode : '',
              suburb: primaryAddress ? primaryAddress.Place : '',
              number: primaryAddress ? primaryAddress.No : '',
            },
            primaryCountryCode: countryCodes(lookupCountryCode(ah.PhonePrimary), ah.PrimaryCountryCode) as CountryCodes,
            secondaryCountryCode: countryCodes(lookupCountryCode(ah.PhoneSecondary), ah.SecondaryCountryCode) as CountryCodes,
            primaryPhoneNumber: ah.PhonePrimary,
            secondaryPhoneNumber: ah.PhoneSecondary,
            hasRecentMove: ah.HasMoved,
            previousAddress: ah.HasMoved
              ? {
                  address: formerAddress ? formerAddress.Street : '',
                  country: formerAddress
                    ? findMetadataOption(
                        formerAddress.CountryId,
                        state.metadata.countries,
                      )
                    : null,
                  postcode: formerAddress ? formerAddress.PostCode : '',
                  suburb: formerAddress ? formerAddress.Place : '',
                  number: formerAddress ? formerAddress.No : '',
                }
              : null,
            emailAddress: ah.EmailPrimary,
            secondaryEmailAddress: ah.EmailSecondary,
            primaryNumber: ah.PhonePrimary,
            fax: ah.Fax,
            dobDate: dob.getDate().toString().padStart(2, '0'),
            dobMonth: (dob.getMonth() + 1).toString().padStart(2, '0'),
            dobYear: dob.getFullYear().toString(),
            placeOfBirth: ah.BirthPlace,
            countryOfBirthId: findMetadataOption(
              ah.BirthCountryId,
              state.metadata.countries,
            ),
            nationalityId: findMetadataOption(
              ah.NationalityId,
              state.metadata.countries,
            ),
            maritalStatusId: findMetadataOption(
              ah.MaritalStatusId,
              state.metadata.maritalStatuses,
            ),
            passportNumber: ah.PassportNumber,
            //
            levelOfEducationId: findMetadataOption(
              ah.EducationLevel,
              state.metadata.levelsOfEducation,
            ),

            countryOfFiscalResidence: {
              country: fiscalResidence
                ? findMetadataOption(
                    fiscalResidence.CountryId,
                    state.metadata.countries,
                  )
                : null,
              reasonForNoTin: findReasonOption(fiscalResidence?.Remarks),
              tin: fiscalResidence ? fiscalResidence.TaxIdentification : '',
            },
            leiRequired: !!ah.LegalEntityIdentifier,
            lei: ah.LegalEntityIdentifier,
            politicallyExposedPersons: ah.PoliticallyExposed,
            IsUSCitizen: ah.IsUSCitizen,
            ReportingTermsAgreed: ah.ReportingTermsAgreed,
            CorrespondenceTermsAgreed: ah.CorrespondenceTermsAgreed,
            OccupationId: ah.OccupationId
              ? findMetadataOption(ah.OccupationId, state.metadata.occupation)
              : null,
            Occupation: ah.Occupation ? ah.Occupation : '',
            EmploymentStatusId: ah.EmploymentStatusId
              ? findMetadataOption(
                  ah.EmploymentStatusId,
                  state.metadata.employmentStatus,
                )
              : null,
            EmployerName: ah.EmployerName,
            EmployerNumber: ah.EmployerNumber,
            DurationOfEmployment: ah.DurationOfEmployment ?? null,
            SectorOfEmployment: ah.SectorOfEmployment ?? null,
            RetiredRemarks: ah.RetiredRemarks ?? null,
            VATNumber: ah.VATNumber,
            agreeToBusinessConditionTerms: ah.BusinessConditionsTerms,
          };
        };

        if (primaryAccountHolder) {
          state.personalDetails.primaryAccountHolder = accountHolderFromResponse(
            primaryAccountHolder,
          );
        }

        if (secondaryAccountHolder) {
          state.personalDetails.secondaryAccountHolder = accountHolderFromResponse(
            secondaryAccountHolder,
          );
        }

        if (alternativeMailingAddress) {
          state.personalDetails.alternativeMailingAddress = {
            address: alternativeMailingAddress ? alternativeMailingAddress.Street : '',
            country: alternativeMailingAddress
              ? findMetadataOption(
                alternativeMailingAddress.CountryId,
                  state.metadata.countries,
                )
              : null,
            postcode: alternativeMailingAddress ? alternativeMailingAddress.PostCode : '',
            suburb: alternativeMailingAddress ? alternativeMailingAddress.Place : '',
            number: alternativeMailingAddress ? alternativeMailingAddress.No : '',
          },
          state.personalDetails.usePrimaryMailingAddress = false;
          state.personalDetails.useSecondaryMailingAddress = false;

        }

        // Reconcile disbursement accounts.
        state.personalDetails.disbursementAccounts = response.Data.DisbursementAccounts.map(
          (entry) => {
            const currencyFound = state.metadata.currencies.find(
              (currency: { value: string }) =>
                currency.value === entry.CurrencyId,
            );
            const countryFound = state.metadata.countries.find(
              (country: { value: string }) => country.value === entry.CountryId,
            );
            const RegularPaymentsCurrency = entry.RegularPayments_Currency ? state.metadata.currencies.find(
              (currency: { text: string }) =>
                currency.text === entry.RegularPayments_Currency,
            ) : null;
            const DirectDebitCurrency: Interval = entry.DirectDebit_Currency ? state.metadata.currencies.find(
              (currency: { text: string }) => currency.text === entry.DirectDebit_Currency,
            ) : null;
            const DirectDebitFrequency: Interval | undefined = frequency.find(
              (interval: { value: number }) => interval.value === entry.DirectDebit_Frequency,
            );
            const RegularPaymentsFrequency: Interval | undefined = frequency.find(
              (interval: { value: number }) => interval.value === entry.RegularPayments_Currency,
            );

            return {
              id: entry.ID,
              currencyId: currencyFound ? currencyFound : null,
              name: entry.AccountName,
              iban: entry.IBAN,
              bankSortCode: entry.SWIFT,
              bankName: entry.BankName,
              country: countryFound ? countryFound : null,
              DirectDebitDetails: {
                active: entry.DirectDebit,
                amount: entry.DirectDebit_Amount,
                currency: DirectDebitCurrency ? DirectDebitCurrency : null,
                date: entry.DirectDebit_Date,
                frequency: DirectDebitFrequency ? DirectDebitFrequency : null,
              },
              RegularPaymentsDetails: {
                active: entry.RegularPayments,
                amount: entry.RegularPayments_Amount,
                currency: RegularPaymentsCurrency,
                date: entry.RegularPayments_Date,
                frequency: RegularPaymentsFrequency ? RegularPaymentsFrequency : null,
              },
            };
          },
        );
        // Reconcile investment profile.
        if (response.Data.InvestmentProfile) {
          const actual = response.Data.InvestmentProfile;

          const currencyFound = state.metadata.currencies.find(
            (entry: { value: any }) => entry.value === actual.CurrencyId,
          );

          const findPastService = (
            label: OpenAccountInvestmentServiceOption['value'] | string,
          ) => {
            const value = state.metadata.investmentServices.find(
              (entry: { value: number }) => entry.value === label,
            );

            if (!value) {
              throw new Error('Unknown past experience entry from server.');
            }

            return value;
          };

          const pastServices: OpenAccountInvestmentServiceOption[] = [];

          if (actual.InvestmentAdvice) {
            pastServices.push(findPastService('advice'));
          }
          if (actual.OrdersWithoutAssistance) {
            pastServices.push(findPastService('orders'));
          }
          if (actual.PortfolioManagement) {
            pastServices.push(findPastService('management'));
          }

          state.investmentProfile = {
            ...state.investmentProfile,
            NotInterested: response.Data.InvestmentProfile.NotInterested,
            NotSufficient: response.Data.InvestmentProfile.NotSufficient,
            Sufficient: response.Data.InvestmentProfile.Sufficient,
            NaturalPersonTerms:
              response.Data.InvestmentProfile.NaturalPersonTerms,
            Appropriatenesses: response.Data.Appropriateness,
            id: actual.ID,
            seekExemption: actual.AppropriatenessExemption,
            investmentCurrency: currencyFound ? currencyFound : null,
            investmentAmount: parseInt(actual.DisposableAssetsRangeId, 10),
            PlannedInvestmentFrequency: actual.PlannedInvestmentFrequency || state.investmentProfile.PlannedInvestmentFrequency,
            PlannedInvestmentFrequencyOther: actual.PlannedInvestmentFrequencyOther || state.investmentProfile.PlannedInvestmentFrequencyOther,
            SourceOfFunds: response.Data.SourceOfFunds.map(
              (entry: {
                FundTypeId: any;
                ID: any;
                Proceeds: { toString: () => any };
                Remarks: any;
                IncomeSaleId: any;
                SelfEmployedRemarks: any;
                RetiredRemarks: any;
              }) => {
                let value;
                value = state.metadata.sourceOfFundIncomeSources.find(
                  (lookup: any) => lookup.value === entry.FundTypeId,
                );
                if (!value) {
                  value = state.metadata.otherSourceFunds.find(
                    (lookup: any) => lookup.value === entry.FundTypeId,
                  );
                }
                if (!value) {
                  value = state.metadata.incomeSources.find(
                    (lookup: any) => lookup.value === entry.FundTypeId,
                  );
                }

                return {
                  ID: entry.ID || 0,
                  value: value ? value : null,
                  proceeds: entry.Proceeds.toString(),
                  remarks: entry.Remarks,
                  IncomeSaleId: entry.IncomeSaleId,
                  SelfEmployedRemarks: entry.SelfEmployedRemarks,
                  RetiredRemarks: entry.RetiredRemarks,
                };
              },
            ),
            incomeSources: response.Data.IncomeSources.map((entry) => {
              const value = state.metadata.refrenceIncomSources.find(
                (lookup: any) => lookup.value === entry.IncomeSourceId,
              );

              return {
                value: value ? value : null,
                proceeds: entry.Proceeds.toString(),
                remarks: entry.Relationship,
              };
            }),
            investmentProfile: parseInt(actual.RiskProfileId, 10),
            pastInvestmentServices: pastServices,
            agreeToAppropriatenessTestTerms: actual.AppropriatenessTestTerms,
          };
        }

        // Reconcile fees and charges.
        if (response.Data.FeesCharges) {
          const actual = response.Data.FeesCharges;

          state.feesAndCharges = {
            ...response.Data.FeesCharges,
            id: actual.ID,
            customFeeStructure: actual.IsCustom,
            platformProcessingFee: actual.PlatformProcessingFee
              ? actual.PlatformProcessingFee
              : 0,
            annualAccountFeeAdministration: actual.AnnualAccountFee
              ? actual.AnnualAccountFee
              : 0,
            annualAccountFeeRenumeration: actual.AnnualAccountFeeMoventum
              ? actual.AnnualAccountFeeMoventum
              : 0,
            annualServiceFee: actual.AccountServiceFee
              ? actual.AccountServiceFee
              : 0,
            initialFee: actual.InitialFee ? actual.InitialFee : 0,
            etfEntryFee: actual.ETFEntryFee ? actual.ETFEntryFee : 0,
            equitiesEntryFee: actual.EquitiesEntryFee
              ? actual.EquitiesEntryFee
              : 0,
            structuredNotesEntryFee: actual.NotesEntryFee
              ? actual.NotesEntryFee
              : 0,
            minimumCashBalance: actual.MinimumCashBalance
              ? actual.MinimumCashBalance
              : 0,
            IsTermAccount:
              state.accountTypes[0].instantAccessOrTermAccount?.value === 2
                ? true
                : false,
          };
        }

        // Reconcile acknowledgements.
        if (response.Data.Acknowledgements) {
          const actual = response.Data.Acknowledgements;

          const discretionaryAssetManager = state.metadata.discretionaryAssetManagers.find(
            (item: { value: number }) =>
              actual.DiscretionaryAssetManager &&
              item.value === parseInt(actual.DiscretionaryAssetManager, 10),
          );

          state.acknowledgements = {
            id: actual.ID,
            discretionaryAssetManager: discretionaryAssetManager
              ? discretionaryAssetManager
              : null,
            agreeToDiscretionaryManagementTerms:
              actual.DiscretionaryManagementTerms,

            AppointDAM: actual.DAMExist,
          };
        }

        // Reconcile Signing PDFs
        state.signingPDFs = response.Data.SigningOrders;

        // Generated manual PDFs
        (state.generatedAccountOpeningFiles as any) =
          response.Data.AccountOpeningFormsURL;
        state.isManual = response.Data.IsManual;
        state.isManualReview = response.Data.IsManual;

        state.TaxCertForm = response.Data.TaxCertForm;
        state.SOFForm = response.Data.SOFForm;

        // Reconcile Uploaded Documents
        state.uploadedDocuments = response.Data.Documents;
      } catch (e) {
        console.log('Error found', e);
      }
    },

    addAccountType(state): void {
      state.accountTypes = state.accountTypes.concat({
        accountType: null,
        trustee: null,
        dfmProvider: null,
        instantAccessOrTermAccount: null,
        classicOrFlexibleFeeSchedule: null,
        IsTermAccount: false,
      });
    },

    removeAccountType(state, index: number): void {
      state.accountTypes = state.accountTypes.filter((v, i) => i !== index);
    },

    addSecondaryAccountHolder(state): void {
      state.personalDetails.secondaryAccountHolder = {
        title: null,
        firstName: '',
        lastName: '',
        address: {
          address: '',
          suburb: '',
          postcode: '',
          country: null,
          number: '',
        },

        primaryCountryCode:   {
          name: 'Canada',
          value: '+1',
          text: '+1',
        },
        secondaryCountryCode:   {
          name: 'Canada',
          value: '+1',
          text: '+1',
        },
        Occupation: '',
        agreeToBusinessConditionTerms: false,
        hasRecentMove: false,
        primaryNumber: '',
        previousAddress: null,
        emailAddress: '',
        secondaryEmailAddress: '',
        primaryPhoneNumber: '',
        secondaryPhoneNumber: '',
        fax: '',
        dobDate: '',
        dobMonth: '',
        dobYear: '',
        placeOfBirth: '',
        countryOfBirthId: null,
        nationalityId: null,
        passportNumber: null,
        maritalStatusId: null,
        levelOfEducationId: null,
        countryOfFiscalResidence: {
          country: null,
          tin: '',
          reasonForNoTin: null,
        },
        leiRequired: false,
        lei: '',
        politicallyExposedPersons: false,
        IsUSCitizen: false,
        ReportingTermsAgreed: false,
        CorrespondenceTermsAgreed: false,
        OccupationId: null,
        EmploymentStatusId: null,
        EmployerName: '',
        EmployerNumber: '',
        DurationOfEmployment: null,
        SectorOfEmployment: null,
        RetiredRemarks: null,
        VATNumber: '',
      };
    },

    removeSecondaryAccountHolder(state): void {
      state.personalDetails.secondaryAccountHolder = null;
    },

    addTentativeDisbursementAccount(state): void {
      state.personalDetails.tentativeDisbursementAccounts = state.personalDetails.tentativeDisbursementAccounts.concat(
        {
          currencyId: null,
          name: '',
          iban: '',
          bankSortCode: '',
          bankName: '',
          country: null,
          DirectDebitDetails: {
            active: false,
            amount: 0,
            currency: null,
            date: null,
            frequency: null,
          },
          RegularPaymentsDetails: {
            active: false,
            amount: 0,
            currency: null,
            date: null,
            frequency: null,
          },
        },
      );
    },

    removeTentativeDisbursementAccount(state, index: number): void {
      state.personalDetails.tentativeDisbursementAccounts = state.personalDetails.tentativeDisbursementAccounts.filter(
        (v, i) => i !== index,
      );
    },

    useDefaultFeesAndCharges(state, value: boolean): void {
      state.feesAndCharges = {
        ...state.metadata.defaultFeesAndCharges,
        IsTermAccount: state.feesAndCharges.IsTermAccount,
        customFeeStructure: !value,
      };
    },
    updateQuarterlyAdminCharges(state, value: number): void {
      state.feesAndCharges.TA_ProductFeeQuarterServiceFee = value;
      state.feesAndCharges.TA_BusinessConditionInitialPremiumQuarter = value;

    },
    updateAccountTermConfirmation(state, value: number): void {
      state.feesAndCharges.TA_ProductFeeEstablishPeriod = value;
    },
    updateInitialFee(state, value: number): void {
      state.feesAndCharges.TA_ProductFeeInitialFee = value;
    },
    updateEarlyEncashYear(state, value: number): void {
      state.feesAndCharges.TA_BusinessConditionEarlyEncashYear = value;
    },
    updateInitialPremiumQuarterYear(state, value: number): void {
      state.feesAndCharges.TA_BusinessConditionInitialPremiumQuarterYear = value;
    },
    updateInitialPremium(state, value: number): void {
      state.feesAndCharges.TA_BusinessConditionInitialPremium = value;
    },

    addIncomeSource(state, value: OpenAccountMetadataOption): void {
      state.investmentProfile.incomeSources = state.investmentProfile.incomeSources.concat(
        {
          value,
          proceeds: value.remarks || '',
          remarks: value.proceeds || '',
        },
      );
    },

    removeIncomeSource(state, value: OpenAccountMetadataOption): void {
      state.investmentProfile.incomeSources = state.investmentProfile.incomeSources.filter(
        (entry) => {
          return entry.value !== value;
        },
      );
    },

    addFundSource(
      state,
      {
        value,
        subSource,
        mainSource,
      }: {
        value: OpenAccountMetadataOption;
        subSource: boolean;
        mainSource: OpenAccountMetadataOption;
      },
    ): void {
      if (subSource) {
        const itemIndex = state.investmentProfile.SourceOfFunds.findIndex(
          (sof) => {
            if (typeof sof.value.value === 'number') {
              return sof.value.value === mainSource.value;
            }
          },
        );
        const item = state.investmentProfile.SourceOfFunds[itemIndex];

        if (item && !item.IncomeSaleId) {
          item.IncomeSaleId = value.value;
          state.investmentProfile.SourceOfFunds = state.investmentProfile.SourceOfFunds.concat(
            item,
          );
          state.investmentProfile.SourceOfFunds.splice(itemIndex, 1);
        } else {
          // if the income sale is already choosen
          if (item.IncomeSaleId === value.value) {
            return;
          }
          state.investmentProfile.SourceOfFunds = state.investmentProfile.SourceOfFunds.concat(
            {
              value: mainSource as any,
              IncomeSaleId: value.value,
              proceeds: value.remarks || '',
              remarks: value.proceeds || '',
              // value: { value: '' },
            },
          );
        }
        return;
      }

      state.investmentProfile.SourceOfFunds = state.investmentProfile.SourceOfFunds.concat(
        {
          value: value as any,
          proceeds: value.remarks || '',
          remarks: value.proceeds || '',
        },
      );
    },

    removeFundSource(
      state,
      {
        value,
        subSource,
        mainSource,
      }: {
        value: OpenAccountMetadataOption;
        subSource: boolean;
        mainSource: OpenAccountMetadataOption;
      },
    ): void {
      if (subSource) {
        const item = state.investmentProfile.SourceOfFunds.find((sof) => {
          if (typeof sof.value.value === 'number') {
            return sof.IncomeSaleId === value.value;
          }
        });
        if (item) {
          state.investmentProfile.SourceOfFunds = state.investmentProfile.SourceOfFunds.filter(
            (entry) => {
              return entry.IncomeSaleId !== item.IncomeSaleId;
            },
          );
        }
        return;
      }

      state.investmentProfile.SourceOfFunds = state.investmentProfile.SourceOfFunds.filter(
        (entry: any) => {
          return entry.value !== value;
        },
      );
    },

    addAppropriatenesses(state, {appropriateness, appropriatenessSection}): void {
      const result = Object.values(
        [].concat(state.investmentProfile.Appropriatenesses as any, appropriatenessSection)
          .reduce((r: any, c: any) => (r[c.AppropriatenessId] = Object.assign((r[c.AppropriatenessId] || {}), c), r), {}),
      ).filter((item: any) => item.FirstHolder || item.SecondHolder);

      state.investmentProfile.Appropriatenesses = result as any;
    },

    removeAppropriatenesses(
      state,
      appropriatenessToRemove: Appropriateness,
    ): void {
      state.investmentProfile.Appropriatenesses = state.investmentProfile.Appropriatenesses.filter(
        (apropriatenes) =>
          apropriatenes.AccountOpeningId !==
          appropriatenessToRemove.AppropriatenessId,
      );
    },

    addPreviousExperience(state, value: OpenAccountMetadataOption): void {
      state.investmentProfile.previousExperience = state.investmentProfile.previousExperience.concat(
        {
          value,
          experience: null,
          transactions: null,
          averageVolume: null,
        },
      );
    },

    removePreviousExperience(state, value: OpenAccountMetadataOption): void {
      state.investmentProfile.previousExperience = state.investmentProfile.previousExperience.filter(
        (entry) => entry.value !== value,
      );
    },

    addPastInvestmentService(
      state,
      value: OpenAccountInvestmentServiceOption,
    ): void {
      state.investmentProfile.pastInvestmentServices = state.investmentProfile.pastInvestmentServices.concat(
        value,
      );
    },

    removePastInvestmentService(
      state,
      value: OpenAccountInvestmentServiceOption,
    ): void {
      state.investmentProfile.pastInvestmentServices = state.investmentProfile.pastInvestmentServices.filter(
        (entry) => entry !== value,
      );
    },

    resetAccountOpening(state): void {
      Object.assign(state, defaultState());
    },

    toggleNavigation(state, value: boolean) {
      state.navigationActive = value;
    },


    setDiscretionaryAssetManager(state, data) {
      state.acknowledgements.discretionaryAssetManager = data;
    },

    setUploadedDocuments(state, data) {
      state.uploadedDocuments = data;
    },
    setIsGeneratingPDF(state, data) {
      state.isGeneratingPDF = data;
    },
    setIsSignReview(state, data) {
      state.isSignReview = data;
    },
    setIsManualReview(state, data) {
      state.isManualReview = data;
    },
    setGeneratedAccountOpeningFiles(state, data) {
      state.generatedAccountOpeningFiles = data;
    },
    toggleSourceOfFundsTable(state, data) {
      state.sourceOfFundsModal = data;
    },
    toggleAdvisorList(state) {
      state.accountProvisioning.isAdvisorListOpen = !state.accountProvisioning.isAdvisorListOpen;
    },
    setSelectedBostonId(state, value: number) {
      state.accountProvisioning.selectedBostonId = value;
    },
    resetAdvisorList(state) {
      state.accountProvisioning.selectedBostonId = null;
      state.accountProvisioning.isAdvisorListOpen = false;
    },
    toggleKYCDisclaimerModal(state) {
      state.kyc.isModalOpen = !state.kyc.isModalOpen;
    },
    setAcknowledgeKYC(state, value: boolean) {
      state.kyc.isAcknowledged = value;
    },
    setFieldErrors(state, payload: Record<string, string>) {
      state.fieldErrors = payload;
    },
    clearFieldError(state, field: string) {
      if (state.fieldErrors[field]) {
        delete state.fieldErrors[field];
      }
    }
  },
  actions: {
    setAccountOpeningResponse(
      { commit },
      response: AccountOpeningResponse,
    ): void {
      commit('setAccountOpeningResponse', response);
    },

    getAmaFeeOptionsAllowed({ commit, rootState }) {
      const ifa_AIN = '866';
      const ifa_AMA = '1069';
      const includedCompanies = [ifa_AIN, ifa_AMA];
      return (
        this.state.account.details.whiteLabelId &&
        includedCompanies.includes(this.state.account.details.whiteLabelId)
      );
    },

    async loadExistingAccountOpeningForms(
      { commit },
      bostonId: number,
    ): Promise<readonly AccountOpeningResponse[]> {
      try {
        const response: any = await client.getExistingAccountOpeningForms({
          BostonID: bostonId,
          accountOpeningId: 0,
        });
        commit('setExistingForms', response.Data);
        return response.Data;
      } catch (error) {
        if (error instanceof Error) {
          err(`Failed to load existing forms: ${error.message}`);
        }
      }
      return [];
    },
    async resetAccountOpening({commit}) {
      commit('resetAccountOpening');
      return;
    },

    async getExistingAccountOpeningForm(
      { commit },
      { accountOpeningId, bostonId },
    ): Promise<readonly AccountOpeningResponse[]> {
      try {
        const response: any = await client.getExistingAccountOpeningForms({
          BostonID: bostonId,
          accountOpeningId,
        });
        return response.Data;
      } catch (error) {
        if (error instanceof Error) {
          err(`Failed to load existing forms: ${error.message}`);
        }
      }
      return [];
    },

    async getDefaultFeesAndCharges({ commit, state }, whiteLabelId: number) {
      const defaultFeesAndCharges: any = await client.getDefaultFees({
        whiteLabelID: whiteLabelId,
      });


      if (
        state.accountTypes?.[0]?.instantAccessOrTermAccount?.value &&
        state.accountTypes?.[0]?.classicOrFlexibleFeeSchedule?.value
      ) {
        const feeId = getFeeSchedule(
          state.accountTypes?.[0]?.instantAccessOrTermAccount.value,
          state.accountTypes?.[0]?.classicOrFlexibleFeeSchedule.value,
        );


        const defaultCharges: any = defaultFeesAndCharges.Data.filter(
          (fees: any) => fees.FeeScheduleId === feeId,
        )[0];

        state.metadata.defaultFeesAndCharges = {
          ...state.metadata.defaultFeesAndCharges,
          platformProcessingFee: defaultCharges.PlatformProcessingFee,
          annualAccountFeeAdministration: defaultCharges.AnnualAccountFee,
          annualAccountFeeRenumeration: defaultCharges.AnnualAccountFeeMoventum,
          annualServiceFee: defaultCharges.AccountServiceFee,
          initialFee: defaultCharges.InitialFee,
          etfEntryFee: defaultCharges.ETFEntryFee,
          equitiesEntryFee: defaultCharges.EquitiesEntryFee,
          structuredNotesEntryFee: defaultCharges.NotesEntryFee,
          minimumCashBalance: defaultCharges.MinimumCashBalance,
          TA_BusinessConditionCMA: defaultCharges.TA_BusinessConditionCMA,
          TA_BusinessConditionInitialPremium: defaultCharges.TA_BusinessConditionInitialPremium,
          TA_BusinessConditionInitialPremiumQuarter: defaultCharges.TA_BusinessConditionInitialPremiumQuarter,
          TA_PPEDeductionFee: defaultCharges.TA_PPEDeductionFee,
          TA_PPEDeductionFeeYears: 10,
          TA_ProductFeeInitialFee: defaultCharges.TA_ProductFeeInitialFee,
          TA_ProductFeeQuarterAdminCharge: defaultCharges.TA_ProductFeeQuarterAdminCharge,
          TA_ProductFeeQuarterEncashPerc: defaultCharges.TA_ProductFeeQuarterEncashPerc,
          TA_ProductFeeQuarterServiceFee: defaultCharges.TA_ProductFeeQuarterServiceFee,
        };
      }
    },

    async loadAccountOpeningMetadata({ commit, state }, whiteLabelId: number) {
      const responses = await Promise.all([
        client.getAccountOpeningMetadata({
          whiteLabelID: whiteLabelId,
        }),
      ]);

      const trusteeRequiredAccountTypes: readonly number[] = [
        400160,
        400166,
        400164,
      ];

      const dfmProviderRequiredAccountTypes: readonly number[] = [
        400160,
        400161,
      ];

      const getAccountOpeningFields = (
        field: AccountOpeningMetadataResponse['Data'][0]['Field'],
      ) => {
        return responses[0].Data.filter((entry) => entry.Field === field);
      };

      const metadata: Partial<OpenAccountMetadata> = {
        accountTypes: getAccountOpeningFields('Account Type').map((entry) => ({
          value: entry.Value,
          text: entry.Text.trim(),
          metadata: {
            description: entry.Description,
            showTrustees: trusteeRequiredAccountTypes.includes(entry.Value),
            showDfmProviders: dfmProviderRequiredAccountTypes.includes(
              entry.Value,
            ),
          },
        })),
        investmentServices: getAccountOpeningFields('Investment Services').map(
          (entry) => ({
            value: entry.Value,
            text: entry.Text.trim(),
            metadata: {},
          }),
        ),
        trustees: getAccountOpeningFields('Trustee').map((entry) => ({
          value: entry.Value,
          text: entry.Text.trim(),
          metadata: {},
        })),
        incomeSources: getAccountOpeningFields('Income Sources').map(
          (entry) => ({
            value: entry.Value,
            text: entry.Text.trim(),
            metadata: {},
          }),
        ),
        averageNumberOfTransactions: getAccountOpeningFields(
          'Appropriateness Test Average Number of Transaction',
        ).map((entry) => ({
          value: entry.Value,
          text: entry.Text.trim(),
          metadata: {},
        })),
        averageVolumeOfTransactions: getAccountOpeningFields(
          'Appropriateness Test Average Volume of Transaction',
        ).map((entry) => ({
          value: entry.Value,
          text: entry.Text.trim(),
          metadata: {},
        })),
        sourceOfFundIncomeSources: getAccountOpeningFields(
          'Source of Fund Income Sources',
        ).map((entry) => ({
          value: entry.Value,
          text: entry.Text.trim(),
          metadata: {},
        })),
        otherSourceFunds: getAccountOpeningFields('Source Of Fund - Other').map(
          (entry) => {
            return {
              value: entry.Value,
              text: entry.Text.trim(),
              Description: entry.Description,
              metadata: {},
            };
          },
        ),
        refrenceIncomSources: getAccountOpeningFields(
          'Reference Income Sources',
        ).map((entry) => ({
          value: entry.Value,
          text: entry.Text.trim(),
          metadata: {},
        })),
        previousExperiences: getAccountOpeningFields('Previous Experience').map(
          (entry) => ({
            value: entry.Value,
            text: entry.Text.trim(),
            Description: entry.Description,
            metadata: {},
          }),
        ),
        dfmProviders: getAccountOpeningFields('DFM Provider').map((entry) => ({
          value: entry.Value,
          text: entry.Text.trim(),
          metadata: {},
        })),
        titles: getAccountOpeningFields('Title').map((field) => ({
          value: field.Value,
          text: field.Text,
          metadata: {},
        })),
        riskProfile: getAccountOpeningFields('Risk Profile').map((field) => {
          return {
            value: field.Value,
            text: field.Text,
            Description: field.Description,
            metadata: {},
          };
        }),
        disposableNetAssets: getAccountOpeningFields(
          'Disposable Net Assets',
        ).map((field) => ({
          value: field.Value,
          text: field.Text,
          metadata: {},
        })),
        currencies: getAccountOpeningFields('Currency').map((field) => ({
          value: field.Value,
          text: field.Text,
          metadata: {},
        })),
        countries: getAccountOpeningFields('Country').map((field) => ({
          value: field.Value,
          text: field.Text,
          metadata: {},
        })),
        maritalStatuses: getAccountOpeningFields('Marital Status').map(
          (field) => ({
            value: field.Value,
            text: field.Text,
            metadata: {},
          }),
        ),
        occupation: getAccountOpeningFields('Occupation').map((field) => ({
          value: field.Value,
          text: field.Text,
          metadata: {},
        })),
        employmentStatus: getAccountOpeningFields('Employment Status').map(
          (field) => ({
            value: field.Value,
            text: field.Text,
            metadata: {},
          }),
        ),
        levelsOfEducation: getAccountOpeningFields('Education Level').map(
          (field) => ({
            value: field.Value,
            text: field.Text,
            metadata: {},
          }),
        ),
        discretionaryAssetManagers: getAccountOpeningFields(
          'Discretionary Account Managers',
        ).map((field) => ({
          value: field.Value,
          text: field.Text,
          metadata: {},
        })),
      };

      commit('setAccountOpeningMetadata', metadata);
    },

    async startAccountOpening({ commit }, data: StartAccountOpeningRequest): Promise<void> {
      log(`Starting the account opening process with BostonID ${data.BostonID}.`);

      try {
        const response = await client.startAccountOpening(data);

        commit('setAccountOpeningResponse', response);
        commit('setCurrentPosition', [1, 1]);
      } catch (error) {
        this.dispatch(
          'addError',
          error?.response?.data?.Message ?? 'Error occured while starting a new account opening',
        );
        commit('setCurrentPosition', [0, 0]);
      }

    },

    async deleteAccountOpening(
      { commit },
      accountOpeningId: number,
    ): Promise<void> {
      log(
        `Deleting account opening process with account opening ID ${accountOpeningId}.`,
      );

      const response = await client.deleteAccount(accountOpeningId);
      commit('updateSnackbarMessage', response.Message);
    },

    async saveAccountTypes({ state, commit, dispatch }): Promise<void> {
      if (!state.accountOpeningResponse) {
        throw new Error('Missing active account opening ID.');
      }

      const response: AccountOpeningResponse | null = null;

      for (const ta of state.accountTypes) {
        try {
          if (!ta.accountType) {
            throw new Error(
              'Missing account type ID for tentative account entry.',
            );
          }

          // 🤦

          const getFeeSchedulesId = () => {
            const classicOrFlexible: number = ta.classicOrFlexibleFeeSchedule
              ? ta.classicOrFlexibleFeeSchedule.value
              : 1;

            const instantOrTerm: number = ta.instantAccessOrTermAccount
              ? ta.instantAccessOrTermAccount.value
              : 1;

            if (instantOrTerm === 2) {
              state.feesAndCharges.IsTermAccount = true;
            }

            if (instantOrTerm === 1 && classicOrFlexible === 1) return 1;
            if (instantOrTerm === 1 && classicOrFlexible === 2) return 2;
            if (instantOrTerm === 1 && classicOrFlexible === 3) return 5;
            if (instantOrTerm === 1 && classicOrFlexible === 7) return 7;

            if (instantOrTerm === 2 && classicOrFlexible === 1) return 3;
            if (instantOrTerm === 2 && classicOrFlexible === 2) return 4;
            if (instantOrTerm === 2 && classicOrFlexible === 3) return 6;

            return 1;
          };

          const request: SaveTentativeAccountRequest = {
            AccountOpeningId: state.accountOpeningResponse.Data.ID,
            AccountTypeId: ta.accountType.value,
            DFMProviderId: ta.dfmProvider ? ta.dfmProvider.value : null,
            FeeSchedulesId: getFeeSchedulesId(),
            QSTProviderId: ta.trustee ? ta.trustee.value : null,
          };

          if (ta.id) {
            log(`Updating account type with ID ${ta.id}.`);
            dispatch(
              'setAccountOpeningResponse',
              await client.updateAccountType(ta.id, request),
            );
          } else {
            log(`Adding ${ta.accountType.text} account type.`);
            dispatch(
              'setAccountOpeningResponse',
              await client.addAccountType(request),
            );
          }
        } catch (error) {
          if (error instanceof Error) {
            err(`Failed to save account info: ${error.message}`);
          }
        }
      }

      if (response) {
        dispatch('setAccountOpeningResponse', response);
      }
    },


    async savePersonalDetails({ state, dispatch }): Promise<readonly Error[]> {
      if (!state.accountOpeningResponse) {
        throw new Error('Missing account opening ID.');
      }

      log('Saving personal details...');
      console.log('PREV', state.personalDetails.primaryAccountHolder);

      const accountOpeningId: number = state.accountOpeningResponse.Data.ID;

      const requestFromAccountHolder = (
        accountHolder: AccountHolderDetails,
        primary: boolean,
      ): AddPersonalDetailsRequest => {
        let correspondenceAddress: AddressDetails | null | undefined = null;

        const reason = accountHolder.countryOfFiscalResidence.reasonForNoTin as any;

        if (primary) {
          correspondenceAddress = !state.personalDetails.usePrimaryMailingAddress && !state.personalDetails.useSecondaryMailingAddress ?
          state.personalDetails.alternativeMailingAddress : null;
        }

        return {
          AccountOpeningId: accountOpeningId,
          IsPrimary: primary,
          TitleId: accountHolder.title ? accountHolder.title.value : null,
          LastName: accountHolder.lastName,
          FirstName: accountHolder.firstName,
          PrimaryStreet: accountHolder.address.address,
          PrimaryPlace: accountHolder.address.suburb,
          PrimaryPostCode: accountHolder.address.postcode,
          PrimaryCountryId: accountHolder.address.country
            ? accountHolder.address.country.value
            : null,
          PrimaryNo: accountHolder.address.number,
          HasMoved: accountHolder.hasRecentMove,
          FormerStreet: accountHolder.previousAddress
            ? accountHolder.previousAddress.address
            : null,
          FormerPlace: accountHolder.previousAddress
            ? accountHolder.previousAddress.suburb
            : null,
          FormerPostCode: accountHolder.previousAddress
            ? accountHolder.previousAddress.postcode
            : null,
          FormerNo: accountHolder.previousAddress
            ? accountHolder.previousAddress.number
            : null,
          FormerCountryId:
            accountHolder.previousAddress &&
            accountHolder.previousAddress.country
              ? accountHolder.previousAddress.country.value
              : null,

          EmailPrimary: accountHolder.emailAddress,
          EmailSecondary: accountHolder.secondaryEmailAddress,
          PhonePrimary: accountHolder.primaryPhoneNumber,
          PhoneSecondary: accountHolder.secondaryPhoneNumber,
          PassportNumber: accountHolder.passportNumber,
          Fax: accountHolder.fax,
          BirthDay: parseInt(accountHolder.dobDate, 10),
          BirthMonth: parseInt(accountHolder.dobMonth, 10),
          BirthYear: parseInt(accountHolder.dobYear, 10),
          BirthPlace: accountHolder.placeOfBirth,
          BirthCountryId: accountHolder.countryOfBirthId
            ? accountHolder.countryOfBirthId.value
            : null,
          NationalityId: accountHolder.nationalityId
            ? accountHolder.nationalityId.value
            : null,
          MaritalStatusId: accountHolder.maritalStatusId
            ? accountHolder.maritalStatusId.value
            : null,
          EducationLevelId: accountHolder.levelOfEducationId
            ? accountHolder.levelOfEducationId.value
            : null,
          FiscalCountryId: accountHolder.countryOfFiscalResidence.country
            ? accountHolder.countryOfFiscalResidence.country.value
            : null,
          TaxNumber: accountHolder.countryOfFiscalResidence.tin,
          TaxRemarks: reason?.value ?? null,
          LegalEntityIdentifier: accountHolder.lei,
          PoliticallyExposed: accountHolder.politicallyExposedPersons,
          // New fields 9/02/2022
          IsUSCitizen: accountHolder.IsUSCitizen,
          ReportingTermsAgreed: accountHolder.ReportingTermsAgreed,
          CorrespondenceTermsAgreed: accountHolder.CorrespondenceTermsAgreed,
          CorrespondencePrimary: state.personalDetails.usePrimaryMailingAddress,
          CorrespondenceSecondary:
            state.personalDetails.useSecondaryMailingAddress,
          CorrespondenceStreet: correspondenceAddress
            ? correspondenceAddress.address
            : null,
          CorrespondenceNo: correspondenceAddress
            ? correspondenceAddress.number
            : null,
          CorrespondencePlace: correspondenceAddress
            ? correspondenceAddress.suburb
            : null,
          CorrespondencePostCode: correspondenceAddress
            ? correspondenceAddress.postcode
            : null,
          CorrespondenceCountryId:
            correspondenceAddress && correspondenceAddress.country
              ? correspondenceAddress.country.value
              : null,
          OccupationId: accountHolder.OccupationId
            ? accountHolder.OccupationId.value
            : null,
          Occupation: accountHolder.Occupation,
          DurationOfEmployment: accountHolder.DurationOfEmployment,
          SectorOfEmployment: accountHolder.SectorOfEmployment,
          RetiredRemarks: accountHolder.RetiredRemarks,
          EmploymentStatusId: accountHolder.EmploymentStatusId
            ? accountHolder.EmploymentStatusId.value
            : null,
          EmployerName: accountHolder.EmployerName,
          EmployerNumber: accountHolder.EmployerNumber,
          // New field 02/05/2023
          VATNumber: accountHolder.VATNumber,
          BusinessConditionsTerms: true,
          // // new field 03/08/2023
          PrimaryCountryCode: accountHolder.primaryCountryCode.name,
          SecondaryCountryCode: accountHolder.secondaryCountryCode.name,
        };
      };

      const errors: Error[] = [];

      try {
        log(`Saving primary account holder details...`);
        const holderDetails: any = [];

        // primary account holder
        let holder = requestFromAccountHolder(
          state.personalDetails.primaryAccountHolder,
          true,
          );

        if (state.personalDetails.primaryAccountHolder.id) {
          holder = { ID: state.personalDetails.primaryAccountHolder.id , ...holder};
        }
        holderDetails.push(holder);
        // secondary account holder
        if (state.personalDetails.secondaryAccountHolder) {
          let secondaryHolder = requestFromAccountHolder(
            state.personalDetails.secondaryAccountHolder,
            false,
          );
          if (state.personalDetails.secondaryAccountHolder.id) {
            secondaryHolder = {ID: state.personalDetails.secondaryAccountHolder.id , ...secondaryHolder};
          }
          holderDetails.push(secondaryHolder);
        }

        // Submit the primary account holder.
        const response = state.personalDetails.primaryAccountHolder.id
          ? await client.updatePersonalDetails(holderDetails)
          : await client.addPersonalDetails(holderDetails);

        log(`account holder details saved.`, response);

        dispatch('setAccountOpeningResponse', response);
      } catch (error) {
        if (error instanceof Error) {
          if (Object.keys(errors).length > 0) {
            err(`Unable to save account holder details: ${error.message}`);
          }
          errors.push(error);
        }
      }

      return errors;
    },

    async saveDisbursementAccount(
      { state, dispatch },
      model: DisbursementAccountDetails,
    ): Promise<readonly Error[]> {
      if (!state.accountOpeningResponse) {
        throw new Error('Missing account opening ID.');
      }

      log(`Saving disbursement account info...`);

      try {
        const response = await client.addDisbursementAccount({
          AccountOpeningId: state.accountOpeningResponse.Data.ID,
          CurrencyId: model.currencyId ? model.currencyId.value : null,
          AccountName: model.name,
          IBAN: model.iban,
          SWIFT: model.bankSortCode,
          BankName: model.bankName,
          CountryId: model.country ? model.country.value : null,
          RegularPayments: model.RegularPaymentsDetails.active,
          RegularPayments_Amount: model.RegularPaymentsDetails.amount,
          RegularPayments_Currency: model.RegularPaymentsDetails.currency ? model.RegularPaymentsDetails.currency.text : null,
          RegularPayments_Date: model.RegularPaymentsDetails.date,
          RegularPayments_Frequency: model.RegularPaymentsDetails.frequency ? model.RegularPaymentsDetails.frequency?.value : null,
          DirectDebit: model.DirectDebitDetails.active,
          DirectDebit_Amount: model.DirectDebitDetails.amount,
          DirectDebit_Currency: model.DirectDebitDetails.currency ? model.DirectDebitDetails.currency.text : null,
          DirectDebit_Date: model.DirectDebitDetails.date,
          DirectDebit_Frequency: model.DirectDebitDetails.frequency ? model.DirectDebitDetails.frequency?.value : null,
        });

        dispatch('setAccountOpeningResponse', response);

        log(`Disbursement account saved.`);
      } catch (error) {
        if (error instanceof Error) return [error];
      }

      return [];
    },

    async removeDisbursementAccount(
      { state, dispatch },
      index: number,
    ): Promise<readonly Error[]> {
      const account: DisbursementAccountDetails =
        state.personalDetails.disbursementAccounts[index];

      try {
        if (account.id) {
          log(`Removing disbursement account with ID ${account.id}...`);

          dispatch(
            'setAccountOpeningResponse',
            await client.removeDisbursementAccount(account.id),
          );

          this.commit('addTentativeDisbursementAccount');

          log(`Removed disbursement account with ID ${account.id}.`);
        }
      } catch (error) {
        if (error instanceof Error) {
          err(`Unable to remove disbursement account: ${error.message}`);
          return [error];
        }
      }

      return [];
    },

    async saveInvestmentProfile({
      state,
      dispatch,
    }): Promise<readonly Error[]> {
      if (!state.accountOpeningResponse) {
        throw new Error('Missing account opening ID.');
      }

      log('Saving investment profile details...');

      const accountOpeningId: number = state.accountOpeningResponse.Data.ID;

      const request: AddInvestmentProfileRequest = {
        AccountOpeningId: accountOpeningId,
        CurrencyId: state.investmentProfile.investmentCurrency
          ? state.investmentProfile.investmentCurrency.value
          : null,
        DisposableAssetsRangeId: state.investmentProfile.investmentAmount,
        RiskProfileId: state.investmentProfile.investmentProfile,
        AppropriatenessExemption: state.investmentProfile.seekExemption,
        NotInterested: state.investmentProfile.NotInterested,
        NotSufficient: state.investmentProfile.NotSufficient,
        Sufficient: state.investmentProfile.Sufficient,
        NaturalPersonTerms: state.investmentProfile.NaturalPersonTerms,
        //
        IncomeSources: state.investmentProfile.incomeSources?.map(
          (source, i) => {
            return {
              ID: i + 1,
              AccountOpeningId: accountOpeningId,
              IncomeSourceId: source.value ? source.value.value : null,
              Proceeds: parseFloat(source.proceeds) || 1,
              Relationship: source.remarks,
            };
          },
        ),
        SourceOfFunds: state.investmentProfile.SourceOfFunds.map(
          (source, i) => {
            const sourceItem: SourceOfFunds = {
              ...source,
              AccountOpeningId: accountOpeningId,
              Proceeds: source.proceeds || '',
              FundTypeId: source.value ? source.value.value : null,
              NetAssetsRangeId: source.value ? source.value.value : null,
              IncomeSaleId: source.IncomeSaleId
                ? source.IncomeSaleId
                : source.value.value
                ? parseInt(source.value.value, 10)
                : null,
              Remarks: source.remarks,
              SelfEmployedRemarks: source.SelfEmployedRemarks || '',
              RetiredRemarks: source.RetiredRemarks || '',
            };
            return sourceItem;
          },
        ),
        Appropriatenesses: state.investmentProfile.Appropriatenesses.map(
          (item) => {
            return {
              ...item,
              AccountOpeningId: accountOpeningId,
              AppropriatenessId: item.AppropriatenessId,
              OtherRemarks: item.OtherRemarks,
              FirstHolder: item.FirstHolder,
              SecondHolder: item.SecondHolder,
            };
          },
        ),
        AppropriatenessTestTerms: state.investmentProfile.agreeToAppropriatenessTestTerms,
        PlannedInvestmentFrequency: state.investmentProfile.PlannedInvestmentFrequency,
        PlannedInvestmentFrequencyOther: state.investmentProfile.PlannedInvestmentFrequencyOther
      };

      const errors: Error[] = [];

      try {
        const response = state.investmentProfile.id
          ? await client.updateInvestmentProfile(
              state.investmentProfile.id,
              request,
            )
          : await client.addInvestmentProfile(request);

        dispatch('setAccountOpeningResponse', response);

        log(`Saved investment profile.`);
      } catch (error) {
        if (error instanceof Error) {
          if (Object.keys(errors).length > 0) {
            err(`Could not save investment profile: ${error.message}`);
            errors.push(error);
          }
        }
      }

      return errors;
    },

    async saveFees({ state, dispatch, rootState }): Promise<readonly Error[]> {
      if (!state.accountOpeningResponse) {
        throw new Error('Missing account opening ID.');
      }

      const extractFeeValue = (input: string | number) => {
        if (typeof input === 'string') {
          const value = parseFloat(input.replace(/[^\d.]/, ''));
          return Number.isFinite(value) ? value : 0;
        }
        return input;
      };

      const amaClassicFlexCtx = isAmaClassicFlexOpenAccountContext(
        rootState.account?.details ?? {},
        state.accountTypes,
      );
      const baselineProcessingFee = Number(
        state.metadata.defaultFeesAndCharges?.platformProcessingFee ?? 0,
      );
      const currentProcessingFee = extractFeeValue(
        state.feesAndCharges.platformProcessingFee,
      );
      const isCustomForAmaClassicFlex =
        amaClassicFlexCtx &&
        !platformProcessingFeesAreEqual(
          currentProcessingFee,
          baselineProcessingFee,
        );

      const isCustom = amaClassicFlexCtx
        ? isCustomForAmaClassicFlex
        : state.feesAndCharges.customFeeStructure;

      let request: AddFeesRequest = {
        AccountOpeningId: state.accountOpeningResponse.Data.ID,
        IsCustom: isCustom,
        PlatformProcessingFee: extractFeeValue(
          state.feesAndCharges.platformProcessingFee,
        ),
        AnnualAccountFee: extractFeeValue(
          state.feesAndCharges.annualAccountFeeAdministration,
        ),
        AnnualAccountFeeMoventum: extractFeeValue(
          state.feesAndCharges.annualAccountFeeRenumeration,
        ),
        AccountServiceFee: extractFeeValue(
          state.feesAndCharges.annualServiceFee,
        ),
        InitialFee: extractFeeValue(state.feesAndCharges.initialFee),
        ETFEntryFee: extractFeeValue(state.feesAndCharges.etfEntryFee),
        EquitiesEntryFee: extractFeeValue(
          state.feesAndCharges.equitiesEntryFee,
        ),
        NotesEntryFee: extractFeeValue(
          state.feesAndCharges.structuredNotesEntryFee,
        ),
        MinimumCashBalance: extractFeeValue(
          state.feesAndCharges.minimumCashBalance,
        ),
      };

      // Add the new TA fields to the request
      if (state.feesAndCharges.IsTermAccount) {
        request = {
          ...state.feesAndCharges,
          ...request,
        };
      }

      const errors: Error[] = [];

      try {
        if (state.feesAndCharges.id) {
          log('Updating fees...');

          const response = await client.updateFees(
            state.feesAndCharges.id,
            request,
          );
          dispatch('setAccountOpeningResponse', response);
        } else {
          log('Adding fees...');

          const response = await client.addFees(request);
          dispatch('setAccountOpeningResponse', response);
        }

        log(`Saved fees.`);
      } catch (error) {
        if (error instanceof Error) {
          err(`Could not save fees: ${error.message}`);
          errors.push(error);
        }
      }

      return errors;
    },

    async saveAcknowledgements({ state, dispatch }): Promise<readonly Error[]> {
      if (!state.accountOpeningResponse) {
        throw new Error('Missing account opening ID.');
      }

      const errors: any = [];

      try {
        const request: AddAcknowledgementsRequest = {
          AccountOpeningId: state.accountOpeningResponse.Data.ID,
          DiscretionaryAssetManager: state.acknowledgements
            .discretionaryAssetManager
            ? state.acknowledgements.discretionaryAssetManager.value
            : null,
          DiscretionaryManagementTerms:
            state.acknowledgements.agreeToDiscretionaryManagementTerms,
          AppointDAM: state.acknowledgements.AppointDAM,
        };

        if (state.acknowledgements.id) {
          log(
            `Updating acknowledgements with ID ${state.acknowledgements.id}...`,
          );

          dispatch(
            'setAccountOpeningResponse',
            await client.updateAcknowledgements(
              state.acknowledgements.id,
              request,
            ),
          );
        } else {
          log('Adding acknowledgements...');

          dispatch(
            'setAccountOpeningResponse',
            await client.addAcknowledgements(request),
          );
        }

        log(`Acknowledgements saved.`);
      } catch (error) {
        if (error instanceof Error) {
          errors.push(error);
        }
      }

      return errors;
    },
    async uploadDocument({ state, commit, dispatch }, data): Promise<void> {
      const res: any = await client
        .uploadDocuments({
          accountOpeningId: data.accountOpeningId,
          documentTypeId: data.documentTypeId,
          file: data.file,
        })
        .then((response) => {
          dispatch('setAccountOpeningResponse', response);
        })
        .catch((error) => {
          const msg = handleError(
            error,
            'Sorry! Looks like something went wrong while Uploading your file',
          );
          dispatch('addError', msg);
        });
    },
    async deleteDocument({ state, commit, dispatch }, data): Promise<void> {
      const res: any = await client
        .deleteDocuments({
          documentId: data,
        })
        .then((response) => {
          dispatch('setAccountOpeningResponse', response);
        })
        .catch((error) => {
          const msg = handleError(
            error,
            'Sorry! Looks like something went wrong while deleting your document',
          );
          dispatch('addError', msg);
        });
    },

    toggleOpenAccountDocumentReviewModal({ state, commit, dispatch }, data: boolean) {
      commit('setIsSignReview', data);
      commit('setIsManualReview', data);
      if (data  === false) {
        commit('setGeneratedAccountOpeningFiles', []);
        commit('setSigneeDetails', []);
      }
    },
    async generateAccountOpeningPDF({ state, commit, dispatch },  data: boolean): Promise<void> {
      commit('setIsGeneratingPDF', true);
      commit('setIsSignReview', false);
      commit('setIsManualReview', false);
      commit('setGeneratedAccountOpeningFiles', []);
      commit('setSigneeDetails', []);
      const res: any = await client
        .generateAccountOpeningPDF({
          AccountOpeningId: state.accountOpeningResponse?.Data.ID,
          IsDocuSign: data,
        })
        .then((response: any) => {
          commit('setIsGeneratingPDF', false);

          dispatch('setAccountOpeningResponse', response);
          if (!data) {
            // !data
          } else {
            // set signee data to display in the section below
            this.dispatch(
              'setSigneeDetails',
              response.Data.PDFInformation[0].SignerDetails,
            );

            const msg = handleError(
              err,
              'Successfully Generated your Docusign Documents! Please check your email inbox to sign your documents with Docusign. You can see the list of signers required for these documents below. Please ensure you complete the signing process before finalising your application below.',
            );
            dispatch('addError', msg);
            this.commit(
              'setSigneeDetails',
              response.Data.PDFInformation[0].SignerDetails,
            );
          }
        })
        .catch((error) => {
          commit('setIsGeneratingPDF', false);
          const msg = handleError(
            error,
            'Sorry! Looks like something went wrong while generating your account opening forms',
          );
          dispatch('addError', msg);
        });
    },
    async setSigneeDetails(state, commit) {
      this.commit('setSigneeDetails', commit);
    },
    async completeAccountOpeningForm({
      state,
      commit,
      dispatch,
    }): Promise<void> {
      const res: any = await client
        .completeAccountOpeningForm({
          accountOpeningId: state.accountOpeningResponse?.Data.ID,
        })
        .then((response: AccountOpeningResponse) => {
          const msg = handleError(
            err,
            'You have completed your account sign up process. You should receive an email from Client Servicing shortly to confirm your details.',
          );
          dispatch('addError', msg);
        })
        .catch((error) => {
          const msg = handleError(
            error,
            'Sorry! Looks like something went wrong while trying to complete your account opening form',
          );
          // dispatch('addError', msg);
        });
    },
    toggleSourceOfFundsTable(state, commit) {
      this.commit('toggleSourceOfFundsTable', commit);
    },
    toggleAdvisorList() {
      this.commit('toggleAdvisorList');
    },
    setSelectedBostonId({ commit }, data) {
      commit('setSelectedBostonId', data);
    },
    resetAdvisorList() {
      this.commit('resetAdvisorList');
    },
    toggleKYCDisclaimerModal({ commit }, data) {
      commit('toggleKYCDisclaimerModal', data);
    },
    setAcknowledgeKYC({ commit }, data) {
      commit('setAcknowledgeKYC', data);
    },
  },
};

export default module;
